import React, {
  ReactNode,
  useContext,
  useEffect,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import isFunction from 'lodash/isFunction';
import groupBy from 'lodash/groupBy';
import IconBadgeButton from 'core/components/IconBadgeButton';
import ToolTip from 'core/components/Tooltip';
import {
  useMedia,
  useMixpanelTrack,
} from 'core/hooks';
import DepositIcon from 'core/icons/deposit';
import InsuranceIcon from 'core/icons/insurance';
import CalendarIcon from 'core/icons/calendar';
import LicenseIcon from 'core/icons/license';
import PrinterIcon from 'core/icons/printer';
import StickyIcon from 'core/icons/sticky';
import TaskIcon from 'core/icons/task';
import {
  GetApiPatientPrintPatientFaceSheetParams,
  PatientDTO,
} from 'dtos';
import {
  getApiPatientPrintPatientFaceSheet,
} from 'endpoints';
import {
  notEmptyString,
} from '@populateui/utils';
import useUploadProfilePicture, {
  usePrintMediaRequest,
} from 'views/EMR/hooks/media';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import useGetPatientMediaByMultimediaType from 'pages/Dashboard/hooks/useGetPatientMediaByMultimediaType';
import usePreviewCarousel from 'pages/Dashboard/hooks/usePreviewCarousel';
import {
  mediaEventChannel,
  patientEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import PatientInfo from 'pages/Dashboard/components/PatientInfo';
import Spinner from 'pages/Dashboard/components/Spinner';
import Skeleton from 'core/components/Skeleton';
import {
  ImageSize,
  Props as ImageProps,
} from 'pages/Dashboard/components/Image';
import WithdrawIcon from 'core/icons/withdraw';
import usePatientSummaryCount from 'pages/Dashboard/hooks/usePatientSummaryCount';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';

type Part = 'root' | 'infoRoot' | 'nameRoot' | 'nameContainer';

type Props = {
  patient: Partial<PatientDTO>;
  onClose?: () => void;
  // Rendered before the close button
  actions?: ReactNode;
  isLoading?: boolean;
  isLink?: boolean;
  infoProps?: ImageProps;
  classes?: Partial<Record<Part, string>>;
  className?: string;
};

const sizeClasses: Record<ImageSize, string> = {
  lg: '!w-16 !h-16',
  md: '!w-10 !h-10',
  xl: '!w-18 !h-18',
};

const iconClasses: Record<ImageSize, string> = {
  lg: '!w-5 !h-5',
  md: '!w-4 !h-4',
  xl: '!w-6 !h-6',
};

export default function PatientHeader({
  patient,
  onClose,
  actions,
  isLoading = false,
  isLink = true,
  classes,
  className,
  infoProps = {},
}: Props) {
  const mixpanelTrack = useMixpanelTrack();
  const { isRestricted: hideRestrictedHeaderActions } = useEMRAccess();
  const {
    extensionExpanded,
    setExtensionExpanded,
    extensionName,
  } = useContext(ViewContext);
  const { patientId, isDeceased, dosespotError } = patient;
  const {
    data: multiMediaResponses = [],
    isFetching: isLoadingMedia,
    refetch: refetchMedia,
  } = useGetPatientMediaByMultimediaType(
    patient?.patientId ?? 0,
    [
      MultimediaType.ProfilePicture,
      MultimediaType.Identification,
      MultimediaType.InsuranceCard,
    ],
    {
      generateUrls: true,
      enabled: !hideRestrictedHeaderActions,
    },
  );

  const { getMediaById } = useMedia();

  const groupedImages = groupBy(multiMediaResponses, 'multimediaType') ?? {};
  const photoIds = groupedImages[MultimediaType.Identification] ?? [];
  const hasID = !isNil(photoIds[0]);
  const insuranceImages = groupedImages[MultimediaType.InsuranceCard] ?? [];

  const onPreviewCarousel = usePreviewCarousel();
  const { print, isPrinting } = usePrintMediaRequest<[GetApiPatientPrintPatientFaceSheetParams]>(
    getApiPatientPrintPatientFaceSheet,
  );

  const printFaceSheet = async () => {
    await print([{ patientId: patient?.patientId }]);
  };

  const { isPending: isUploadingMedia } = useUploadProfilePicture(patient?.patientId ?? 0);

  const hasError = notEmptyString(dosespotError?.title) || isDeceased;

  useEffect(() => {
    const unsubscribeFromUpdate = mediaEventChannel.on('onMediaUpdated', refetchMedia);
    const unsubscribeFromDelete = mediaEventChannel.on('onMediaDeleted', async () => { await refetchMedia(); });
    return () => {
      unsubscribeFromUpdate();
      unsubscribeFromDelete();
    };
  }, [patientId]);

  const { stickyCount, toDoCount, upcomingAppointmentsCount } = usePatientSummaryCount(
    patientId ?? 0,
    { enabled: !hideRestrictedHeaderActions },
  );
  const sizeClass = sizeClasses[infoProps?.size ?? 'lg'];
  const iconClass = iconClasses[infoProps?.size ?? 'lg'];
  const hasProfilePicture = !isNil(getMediaById(patient?.patientId ?? 0));

  const profilePictureTooltip = hasProfilePicture ? 'View/Edit Profile picture' : 'Upload profile picture';
  let profilePictureActionIcon = <RemoveRedEyeOutlinedIcon className={clsx('!text-white', iconClass)} />;
  if (isUploadingMedia) {
    profilePictureActionIcon = <Spinner className={clsx('!text-white', iconClass)} />;
  }

  return (
    <div className={clsx('w-full flex justify-between border-b border-b-gray-300 relative', classes?.root, className)}>
      {isLoading ? (
        <div className="w-full flex items-center p-4 gap-4">
          <Skeleton
            className="!w-16 !h-16"
            variant="rounded"
          />
          <div className="flex flex-col gap-4 flex-1">
            <Skeleton className="!w-52 !h-4" />
            <Skeleton className="!w-28 !h-4" />
          </div>
        </div>
      ) : (
        <>
          <PatientInfo
            patient={(patient ?? {}) as Patient}
            className={clsx('peer/patient flex-1 p-4 overflow-hidden', classes?.infoRoot)}
            classes={classes}
            isLink={isLink}
            image={{
              size: 'lg',
              variant: 'rounded',
              ...infoProps,
            }}
          >
            {!hideRestrictedHeaderActions && (
              <div className="flex items-center">
                <ToolTip
                  title={hasID ? 'Driver License / State ID' : 'No ID Uploaded'}
                >
                  <div className={clsx({
                    'cursor-not-allowed opacity-50': !hasID,
                  })}
                  >
                    <IconButton
                      size="small"
                      disabled={!hasID}
                      onClick={() => { onPreviewCarousel(photoIds); }}
                    >
                      <LicenseIcon className={iconClass} />
                    </IconButton>
                  </div>
                </ToolTip>
                <ToolTip
                  title={insuranceImages.length === 0 ? 'No Insurance Cards Uploaded' : 'Insurance Cards'}
                >
                  <div className={clsx({
                    'cursor-not-allowed opacity-50': insuranceImages.length === 0,
                  })}
                  >
                    <IconButton
                      size="small"
                      onClick={() => { onPreviewCarousel(insuranceImages); }}
                      disabled={insuranceImages.length === 0}
                    >
                      <InsuranceIcon className={iconClass} />
                    </IconButton>
                  </div>
                </ToolTip>
                <ToolTip title="Print Face Sheet">
                  <IconButton
                    size="small"
                    onClick={printFaceSheet}
                    disabled={isLoadingMedia}
                  >
                    {isPrinting
                      ? <Spinner className={iconClass} />
                      : <PrinterIcon className={iconClass} />}
                  </IconButton>
                </ToolTip>
                <IconBadgeButton
                  tooltip="View Upcoming Appointments"
                  badgeContent={upcomingAppointmentsCount}
                  invisible={upcomingAppointmentsCount === 0}
                  Icon={CalendarIcon}
                  classes={{
                    icon: clsx(iconClass, { '!text-blue-500': extensionName === 'upcomingAppointments' }),
                  }}
                  onClick={() => {
                    setExtensionExpanded?.(
                      extensionName === 'upcomingAppointments' ? null : 'upcomingAppointments',
                    );
                  }}
                  color="secondary"
                />
                <IconBadgeButton
                  tooltip="View To-dos"
                  badgeContent={toDoCount}
                  Icon={TaskIcon}
                  classes={{
                    icon: clsx(iconClass, { '!text-blue-500': extensionName === 'todos' }),
                  }}
                  onClick={() => {
                    setExtensionExpanded?.(extensionName === 'todos' ? null : 'todos');
                  }}
                  color="secondary"
                />
                <IconBadgeButton
                  tooltip={extensionExpanded ? 'Hide stickies' : 'Show stickies'}
                  badgeContent={stickyCount}
                  Icon={StickyIcon}
                  onClick={() => {
                    setExtensionExpanded?.(extensionName === 'stickies' ? null : 'stickies');
                  }}
                  classes={{
                    icon: clsx(iconClass, { '!text-blue-500': extensionName === 'stickies' }),
                  }}
                  color="secondary"
                />
                <ToolTip title="Collect Deposit">
                  <IconButton
                    onClick={() => {
                      paymentsEventChannel.emit('onPaymentDialogOpen', {
                        isDeposit: true,
                        patient: patient as Partial<PatientDTO>,
                      });
                    }}
                  >
                    <DepositIcon className={iconClass} />
                  </IconButton>
                </ToolTip>
                <ToolTip title="Adjust patient payment">
                  <IconButton
                    onClick={() => {
                      paymentsEventChannel.emit('onAdjustPayment', {
                        patient: patient as Partial<PatientDTO>,
                      });
                      mixpanelTrack('ClickedAdjustPayment', {
                        patientId: patient?.patientId,
                      });
                    }}
                  >
                    <WithdrawIcon className={iconClass} />
                  </IconButton>
                </ToolTip>
              </div>
            )}
          </PatientInfo>
          {!hideRestrictedHeaderActions && (
            <div className={clsx('!absolute rounded-lg opacity-0 hover:!opacity-100 peer-hover/patient:!opacity-100 peer-focus-visible/patient:!opacity-100 !max-w-[initial] !max-h-[initial]', {
              'left-4 bottom-4': !hasError,
              'left-5 bottom-5': hasError,
              '!bottom-3': hasError && infoProps?.size === 'md',
              '!opacity-100': isUploadingMedia,
            })}
            >
              <ToolTip title={profilePictureTooltip}>
                <IconButton
                  className={clsx('!p-1 !bg-black/50 hover:!bg-black/60 !rounded-lg', sizeClass)}
                  disableRipple
                  onClick={() => {
                    patientEventChannel.emit('onViewProfilePicture', {
                      entityId: patientId ?? 0,
                    });
                  }}
                  disabled={isUploadingMedia}
                >
                  {profilePictureActionIcon}
                </IconButton>
              </ToolTip>
            </div>
          )}
        </>
      )}
      {isFunction(onClose) && (
        <div className="flex items-start gap-0.5 m-3">
          {actions}
          <IconButton
            onClick={onClose}
            className="!w-8 !h-8"
          >
            <CloseIcon className="!h-6 !w-6" />
          </IconButton>
        </div>
      )}
    </div>
  );
}
